import dayjs from "dayjs";
import { IPushbackEntry, IPushbackOrderDetail } from "../../models/configuration/pushback/IPushbackLoans";
import { getPushbackStatusColor } from "../../utils/statusColors";
import { IDataColumn } from "../../components/ui/dataGrid";
import { Chip } from "@mui/material";
import {IDataColumn as LegacyIDataColumn, DTDataGridColumnType } from "../../models/IDataColumn";
import { DtExpandableText } from "../../components/ui/text";

export const orderDetailsColumns = (): Array<IDataColumn> => {
    return [
        {
            name: "Received",
            fieldId: "CreatedTimestamp",
            datatype: 'text',
            sortable: true,
            renderCell: (rowItem: IPushbackOrderDetail) => {
                return dayjs(rowItem.CreatedTimestamp).format('MM/DD/YYYY hh:mm:ss A Z');
            }
        },
        {
            name: "PackageType",
            fieldId: "PackageType",
            datatype: 'text',
            sortable: true
        },
        {
            name: "DeliveryTypes",
            fieldId: "Delivery Types",
            datatype: 'text',
            sortable: false,
            renderCell: (rowItem: IPushbackOrderDetail) => {
                return rowItem.DeliveryTypes.join(", ");
            }
        },
        {
            name: "User",
            fieldId: "EncompassUser",
            datatype: 'text',
            sortable: true
        }
    ];
};

export const pushBackDetailColumns = (
): Array<IDataColumn> => {
    return [
        {
            name: "Received",
            fieldId: "CreatedTimestamp",
            datatype: 'text',
            sortable: true,
            width: 25,
            renderCell: (item: IPushbackEntry) => {
                return dayjs(item.CreatedTimestamp).format('MM/DD/YYYY hh:mm:ss A Z');
            }
        },
        {
            name: "Status",
            fieldId: "Status",
            datatype: 'text',
            sortable: true,
            renderCell: (item: IPushbackEntry) => {
                return <Chip size="small" key={item.Status} label={item.Status} sx={{backgroundColor: getPushbackStatusColor(item.Status), color: 'white'}}/>;
            }
        },
        {
            name: "LastStatusChange",
            fieldId: "LastStatusChangeTimestamp",
            datatype: 'text',
            sortable: true,
            renderCell: (item: IPushbackEntry) => {
                return new Date(item.LastStatusChangeTimestamp).toLocaleString();
            }
        },
        {
            name: "Reason",
            fieldId: "Reason",
            datatype: 'text',
            sortable: true,
            // Business rule: Reasons starting with 'E' (e.g., 'Email') are displayed with a lowercase 'e' and the next character capitalized.
            // This formatting is required to match desktop display rules.
            renderCell: (item: IPushbackEntry) => {
                const reason = item.Reason;
                if (reason && reason.startsWith('E') && reason.length > 1) {
                    return 'e' + reason.charAt(1).toUpperCase() + reason.slice(2);
                }
                return reason;
            }
        }
    ];
};

export const pushBackDetailColumnReport = (
): Array<IDataColumn> => {
    return [
        {
            name: "StartTime",
            fieldId: "CreatedTimestamp",
            datatype: 'text',
            sortable: true,
            renderCell: (row: IPushbackEntry) => {
                return new Date(row.CreatedTimestamp).toLocaleString();
            },
            width: 20
        },
        {
            name: "EndTime",
            fieldId: "LastStatusChangeTimestamp",
            datatype: 'text',
            sortable: true,
            renderCell: (row: IPushbackEntry) => {
                return row?.Status === "Processing" ? "-" : new Date(row.LastStatusChangeTimestamp).toLocaleString();
            },
            width: 20
        },
        {
            name: "Status",
            fieldId: "Status",
            datatype: 'text',
            width: 15
        },
        {
            name: "Message",
            fieldId: "Message",
            datatype: 'text',
            renderCell(row, rowIndex, pageConfig) {
                return <DtExpandableText showToggleLabel={false} previewLength={60} value={row.Message} />;
            },
            sortable: true
        }
    ];
};

export const orderDetailsColumnsLegacy = (
    viewItem: (e: any, item: any) => void,
): Array<LegacyIDataColumn> => {
    return [
        {
            name: "Received",
            fieldId: "CreatedTimestamp",
            datatype: DTDataGridColumnType.text,
            sortable: true,
            dataFunction: (item: IPushbackOrderDetail) => {
                return dayjs(item.CreatedTimestamp).format('MM/DD/YYYY hh:mm:ss A Z');
            }
        },
        {
            name: "PackageType",
            fieldId: "PackageType",
            datatype: DTDataGridColumnType.text,
            sortable: true
        },
        {
            name: "DeliveryTypes",
            fieldId: "Delivery Types",
            datatype: DTDataGridColumnType.text,
            sortable: false,
            dataFunction: (item: IPushbackOrderDetail) => {
                return item.DeliveryTypes.join(", ");
            }
        },
        {
            name: "User",
            fieldId: "EncompassUser",
            datatype: DTDataGridColumnType.text,
            sortable: true
        },
        {
            name: "View Events",
            fieldId: "",
            datatype: DTDataGridColumnType.link,
            sortable: false,
            linkText: "View",
            foreColor: "blue",
            callbackFunction: viewItem,
        },
    ];
};

export const pushBackDetailColumnsLegacy = (
    viewItem: (e: any, item: any) => void,
): Array<LegacyIDataColumn> => {
    return [
        {
            name: "Received",
            fieldId: "CreatedTimestamp",
            datatype: DTDataGridColumnType.text,
            sortable: true,
            dataFunction: (item: IPushbackEntry) => {
                return dayjs(item.CreatedTimestamp).format('MM/DD/YYYY hh:mm:ss A Z');
            }
        },
        {
            name: "Status",
            fieldId: "Status",
            datatype: DTDataGridColumnType.text,
            sortable: true,
            foreColorFunction: (item: IPushbackEntry) => getPushbackStatusColor(item.Status)
        },
        {
            name: "LastStatusChange",
            fieldId: "LastStatusChangeTimestamp",
            datatype: DTDataGridColumnType.text,
            sortable: true,
            dataFunction: (item: IPushbackEntry) => {
                return new Date(item.LastStatusChangeTimestamp).toLocaleString();
            }
        },
        {
            name: "Reason",
            fieldId: "Reason",
            datatype: DTDataGridColumnType.text,
            sortable: true
        },
        {
            name: "View Report",
            fieldId: "",
            datatype: DTDataGridColumnType.link,
            sortable: false,
            linkText: "View",
            foreColor: "blue",
            callbackFunction: viewItem
        },
    ];
};

export const pushBackDetailColumnReportLegacy = (
): Array<LegacyIDataColumn> => {
    return [
        {
            name: "StartTime",
            fieldId: "CreatedTimestamp",
            datatype: DTDataGridColumnType.text,
            sortable: true,
            dataFunction: (item: IPushbackEntry) => {
                return new Date(item.CreatedTimestamp).toLocaleString();
            }
        },
        {
            name: "EndTime",
            fieldId: "LastStatusChangeTimestamp",
            datatype: DTDataGridColumnType.text,
            sortable: true,
            dataFunction: (item: IPushbackEntry) => {
                return new Date(item.LastStatusChangeTimestamp).toLocaleString();
            }
        },
        {
            name: "Status",
            fieldId: "Status",
            datatype: DTDataGridColumnType.text,
            sortable: true,
            foreColorFunction: (item: IPushbackEntry) => getPushbackStatusColor(item.Status)
        },
        {
            name: "Message",
            fieldId: "Message",
            datatype: DTDataGridColumnType.text,
            sortable: true
        }
    ];
};